import { useEffect, useState } from 'react'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { colors, size } from '@styles/theme'
import styled from '@emotion/styled'
import { ActionButton, BottomButtonGroup, ButtonGroupClearFix } from '@components/auth/landing/LandingElements'
import { SheerFormTextField } from '@elements/Inputs'
import StyledAlert from '@elements/StyledAlert'
import axios from '@utils/webRequests'
import usePaymentsApiContext from '@contexts/paymentsApiContext'

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${size(1)};
`

const cardElementOptions = {
  style: {
    base: {
      color: 'white',
      fontSize: '16px'
    },
  }
}

const CardElementContainerOuter = styled.div`
  background-color: rgba(255,255,255,0.1);
  border-radius: 15px;
  border: 1px solid grey;
`

const CardElementContainerInner = styled.div`
  height: 100%;
  width: 100%;
  padding: ${size(2.15)};
  border-radius: 14px;
  border: 1px solid transparent;
`

const UpdatePaymentMethodForm = ({ clientSecret, isLoading, setIsLoading, setIsUpdateSuccessful }) => {
  const [cardBorderEl, setCardBorderEl] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [name, setName] = useState('')
  const elements = useElements()
  const stripe = useStripe()

  useEffect(() => {
    if (!elements || !cardBorderEl) return;
    const cardElement = elements.getElement('card')
    if (cardElement) {
      // adds border emphasis on outline focus
      cardElement.on('focus', () => {
        cardBorderEl.style.border = '1px solid grey'
      })
      // removes card border when unfocused
      cardElement.on('blur', () => {
        cardBorderEl.style.border = '1px solid transparent'
      })
      // cleans up handlers
      return () => cardElement.removeAllListeners()
    }
  }, [elements, cardBorderEl])

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading) return;
    const cardElement = elements.getElement('card')
    // NOTE: Make sure to disable form submission until Stripe.js has loaded.
    if (!stripe || !elements || !cardElement) return;

    // Setup data for request
    const data = {
      payment_method: {
        card: cardElement,
        billing_details: { name, },
      },
    }

    // Sends request to stripe for payment
    // WARNING: the reponse from the confirmCardSetup WILL NOT be caught as an error in a promise, hence using async await
    setIsLoading(true)
    const res = await stripe.confirmCardSetup(clientSecret, data)
    if (res.error) {
      setIsLoading(false)
      setErrorMessage(res?.error?.message)
      console.error(res.error);
    } else {
      const paymentMethodData = { payment_method_id: res.setupIntent.payment_method }
      axios.put(`/api/billing/update_payment_methods`, paymentMethodData)
        .then((res) => {
          setIsLoading(false)
          setErrorMessage(null)
          setIsUpdateSuccessful(true)
        })
    }
  }

  const renderErrorMessage = () => {
    if (errorMessage) return (
      <StyledAlert
        AlertStyles={{ marginBottom: size(5)}}
        kind='error'>
        {errorMessage}
      </StyledAlert>
    )
  }

  return (
    <FormContainer onSubmit={handleSubmit}>
      {renderErrorMessage()}
      <SheerFormTextField
        required
        name='name'
        label='Full Name'
        placeholder='Your name as it appears on card'
        onChange={(e) => setName(e.value)}
        value={name}
      />
      <CardElementContainerOuter >
        <CardElementContainerInner ref={node => node && setCardBorderEl(node)}>
          <CardElement options={cardElementOptions} />
        </CardElementContainerInner>
      </CardElementContainerOuter>
      <ButtonGroupClearFix />
      <BottomButtonGroup style={{ left: 0 }}>
        <ActionButton
          style={{ background: colors.statusColors.right_now('primary') }}
          type='submit'>
          {isLoading ? 'Updating Payment Method...' : 'Submit'}
        </ActionButton>
      </BottomButtonGroup>
    </FormContainer>
  )
}

export default UpdatePaymentMethodForm
